import type { HubConnection } from '@microsoft/signalr';

import { type Nullable, hasValue } from 'utils/values';

/* eslint-disable @typescript-eslint/no-explicit-any -- callback signature mirrors HubConnection.on/off, which is untyped */
type HubEventCallback = (...args: any[]) => void;
/* eslint-enable @typescript-eslint/no-explicit-any */

export interface HubConnectionManagerConfig {
  /** Builds a fresh, unstarted HubConnection (url, protocol, logging, ...) for each (re)connect attempt. */
  buildConnection: () => HubConnection;
  /** Wires up this connection's own message handlers. Called once per built connection, before starting it. */
  bindEvents: (connection: HubConnection) => void;
  /** Seconds to wait between manual retries of the *initial* connection attempt. Defaults to 3. */
  retryInSeconds?: number;
  /** The connection closed - `error` is only set if it wasn't a deliberate `connection.stop()`. */
  onUnexpectedClose?: (error: unknown) => void;
  /** About to attempt the *initial* connection (isRetry: true if a previous attempt in this same round failed). */
  onConnecting?: (isRetry: boolean) => void;
  /** The *initial* connection attempt succeeded. */
  onConnected?: (isRetry: boolean) => void;
  /** The *initial* connection attempt failed and will be retried in `retryInSeconds`. */
  onConnectFailed?: (retryInSeconds: number) => void;
  /** `withAutomaticReconnect()` is attempting to restore a lost connection. */
  onReconnecting?: () => void;
  /** `withAutomaticReconnect()` successfully restored the connection. */
  onReconnected?: () => void;
  /** Automatic reconnection gave up entirely, and we're about to start over from scratch. */
  onRestarting?: () => void;
  /** The from-scratch restart (eventually) succeeded. */
  onRestarted?: () => void;
  /** The from-scratch restart itself failed. */
  onRestartFailed?: (error: unknown) => void;
  /** `connection.stop()` (during Disconnect) threw. */
  onDisconnectError?: (error: unknown) => void;
}

/**
 * One shared `HubConnection` per hub, ref-counted by subscriberId, with:
 * - a manual retry loop for the *first* connection attempt (`withAutomaticReconnect()` only takes
 *   over once a connection has been established at least once), and
 * - a full from-scratch restart if the connection closes and automatic reconnection has given up
 *   entirely - without this, a dropped connection whose built-in retries are exhausted is never
 *   retried again.
 *
 * Shared between SignalRActions and NotificationApiActions so this lifecycle logic (and its retry/
 * restart edge cases) isn't duplicated between the two hubs. Each caller supplies its own
 * `buildConnection`/`bindEvents` plus whichever hooks it needs for UX feedback (e.g. snackbars) -
 * hooks are all optional so a hub with no such UX (NotificationApiActions) can omit them entirely.
 */
export function createHubConnectionManager() {
  let hubConnection: Nullable<Promise<HubConnection>> = null;
  const subscribers = new Set<string>();

  function connect(subscriberId: string, config: HubConnectionManagerConfig): Promise<HubConnection> {
    subscribers.add(subscriberId);

    if (hasValue(hubConnection)) {
      return hubConnection;
    }

    const retryInSeconds = config.retryInSeconds ?? 3;

    hubConnection = new Promise<HubConnection>((resolve) => {
      const connection = config.buildConnection();

      config.bindEvents(connection);

      connection.onclose((error) => {
        config.onUnexpectedClose?.(error);

        // No dependent hooks are listening anymore - no need to restart.
        if (subscribers.size === 0) {
          hubConnection = null;
          return;
        }

        hubConnection = null;
        config.onRestarting?.();

        connect(subscriberId, config)
          .then(() => config.onRestarted?.())
          .catch((restartError: unknown) => config.onRestartFailed?.(restartError));
      });

      connection.onreconnecting(() => config.onReconnecting?.());
      connection.onreconnected(() => config.onReconnected?.());

      let timeoutId: NodeJS.Timeout | null = null;

      // https://learn.microsoft.com/en-us/aspnet/core/signalr/javascript-client?view=aspnetcore-8.0&tabs=visual-studio#automatically-reconnect
      // withAutomaticReconnect only works once the connection has been established at least once,
      // so manually retry it here until the *first* connection attempt succeeds.
      const startConnection = () => {
        const previousTimeoutId = timeoutId;
        const isRetry = previousTimeoutId !== null;

        if (previousTimeoutId !== null) {
          clearTimeout(previousTimeoutId);
          timeoutId = null;
        }

        config.onConnecting?.(isRetry);

        connection
          .start()
          .then(() => {
            config.onConnected?.(isRetry);
            resolve(connection);
          })
          .catch(() => {
            config.onConnectFailed?.(retryInSeconds);
            timeoutId = setTimeout(startConnection, retryInSeconds * 1000);
          });
      };

      startConnection();
    });

    return hubConnection;
  }

  async function disconnect(
    subscriberId: string,
    config: Pick<HubConnectionManagerConfig, 'onDisconnectError'>
  ): Promise<void> {
    if (!hasValue(hubConnection)) {
      return;
    }

    subscribers.delete(subscriberId);

    // If there are still hooks listening, do not disconnect.
    if (subscribers.size > 0) {
      return;
    }

    try {
      const connection = await hubConnection;
      if (connection.state !== 'Disconnected' && connection.state !== 'Disconnecting') {
        await connection.stop();
      }
    } catch (e) {
      config.onDisconnectError?.(e);
    }
  }

  async function on(
    subscriberId: string,
    config: HubConnectionManagerConfig,
    eventName: string,
    callback: HubEventCallback
  ): Promise<void> {
    const connection = await connect(subscriberId, config);
    connection.on(eventName, callback);
  }

  async function off(
    subscriberId: string,
    config: HubConnectionManagerConfig,
    eventName: string,
    callback: HubEventCallback
  ): Promise<void> {
    const connection = await connect(subscriberId, config);
    connection.off(eventName, callback);
  }

  return { connect, disconnect, on, off };
}

export type HubConnectionManager = ReturnType<typeof createHubConnectionManager>;
